import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { unref, computed, ref } from "vue";
import { authenticationService } from "@/core/services";
import { meetingsResetParticipantsSignatureDestroy } from "@sgdf/client";
import type { NotNestedMeetingRole } from "@sgdf/client";
import {
  listMeetings,
  listMeetingTypes,
  retrieveMeeting,
  retrievePublicMeeting,
  syncMeetingFromIntranet,
} from "@sgdf/client-v2";
import { apiCall, MAX_PAGE_LIMIT } from "@/core/utils/api";
import { useMeetingId } from "./routes";
import { DateHelpers } from "@sgdf/ui-library";
import { usePermission } from "./permission";

export const isInMeetingContext = (meetingId: Ref<string | null>) => {
  if (meetingId === null) {
    meetingId = useMeetingId();
  }
  return computed(() => unref(meetingId) != null);
};

export const useCurrentMeeting = () => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();
  return useQuery({
    queryKey: ["meetings", meetingId] as const,
    queryFn: () => apiCall(retrieveMeeting({ path: { id: meetingId.value! } })),
    enabled: () =>
      Boolean(unref(meetingId)) &&
      hasPermission.value("view_meeting", unref(meetingId)),
    staleTime: Infinity,
  });
};

export const usePublicMeeting = () => {
  const meetingId = useMeetingId();
  return useQuery({
    queryKey: ["public-meetings", meetingId] as const,
    queryFn: () =>
      apiCall(retrievePublicMeeting({ path: { id: unref(meetingId)! } })),
    enabled: () => unref(isInMeetingContext(meetingId)),
    staleTime: Infinity,
  });
};

export function useMeetingTypes() {
  return useQuery({
    queryKey: ["meeting-types"],
    queryFn: () => apiCall(listMeetingTypes()),
    staleTime: Infinity,
  });
}

export function useMeetings() {
  return useQuery({
    queryKey: ["meetings"],
    // `/meetings/` est une liste parcourable (limit/offset, défaut 100), mais
    // « Mes stages » la consomme en entier pour la découper en cours/passés :
    // on demande explicitement le plafond plutôt que de subir le défaut.
    queryFn: () => apiCall(listMeetings({ query: { limit: MAX_PAGE_LIMIT } })),
  });
}

export const useIsActive = () => {
  const currentDate = new Date();
  const { data } = usePublicMeeting();

  return computed(() => {
    if (!data.value) {
      return false;
    }
    const startDate = data.value?.start_date;
    const endDate = data.value?.end_date;

    return DateHelpers.isBetween(
      new Date(startDate),
      new Date(endDate),
      currentDate,
    );
  });
};

export const useMeetingRole = async () => {
  const meetingId = useMeetingId();

  const role = ref<NotNestedMeetingRole | null>(null);

  const user = await authenticationService.getUser();
  const currentParticipation = user?.participations.find(
    (p) => p.meeting === unref(meetingId),
  );

  if (currentParticipation) {
    role.value = currentParticipation.role;
  }
  return role;
};

export function useResetSignatures() {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiCall(
        meetingsResetParticipantsSignatureDestroy({
          path: { id: unref(meetingId)! },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["meetings", unref(meetingId), "participants"],
      });
    },
  });
}

export function useSyncMeetingMutation(
  onSuccess?: () => void,
  onError?: () => void,
) {
  const queryClient = useQueryClient();
  const meetingId = useMeetingId();
  return useMutation({
    mutationFn: () =>
      apiCall(syncMeetingFromIntranet({ path: { id: unref(meetingId)! } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings", unref(meetingId)] });
      queryClient.invalidateQueries({
        queryKey: ["meetings", unref(meetingId), "participants"],
      });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
}
